import { Box, Card, Divider, Skeleton, Typography } from '@mui/material';
import { TeamAvailability } from '../components/TeamAvailability';
import { theme } from '@store';
import { DonutChart } from '../components/Charts';
import { ChartCard } from '../components/Cards/ChartCard';
import { useNonAdminView } from './hooks';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Select, TOOLBAR_FIELD_GAP, toolbarFieldSx } from '@ui';
import { ChartsTimeInterval, chartTimeIntervalOptions } from '../types';
import dayjs from 'dayjs';
import React from 'react';
import { chunk } from 'lodash';
import { formatCount } from '@util/number';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import { MultiColorLinearProgress } from '@components';
import { NonAdminDashboardContext } from './contexts/NonAdminViewContext';
import { EXPECTED_DAILY_USER_MINUTES } from '@common/types/constants.ts';
import { Timelapse } from '@mui/icons-material';

export const NonAdminView = () => {
  const {
    user,
    allMetrics,
    taskMetricsList,
    filters,
    handleFilterChange,
    guideTierPatientsEntries,
    guideStatusPatientsEntries,
    serviceLinePatientEntries,
    careManagementPatientEntries,
    memoryConnectTierPatientsEntries,
    memoryConnectStatusPatientsEntries,
    hasViewPermission,
    isMdSizeUp,
    ccmPatientsByMinutes,
    isLoading,
  } = useNonAdminView();

  const contextValues = { filters, handleFilterChange };

  return (
    <NonAdminDashboardContext.Provider value={contextValues}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          gap: '1rem',
          padding: '0 1.5rem',
          justifyContent: 'start',
          '& > *': {
            overflow: 'visible',
          },
          [theme.breakpoints.down('md')]: {
            padding: '0',
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            variant='h4'
            sx={{ textAlign: 'start', width: '100%', mt: 2, mb: 2 }}
          >
            Hello, {user?.first_name}
          </Typography>

          <Box sx={{ display: 'flex', gap: TOOLBAR_FIELD_GAP }}>
            <Select
              name='interval'
              clearable={false}
              options={chartTimeIntervalOptions}
              value={filters?.interval}
              label='Time range'
              sx={{
                bgcolor: theme.palette.common.white,
                ...toolbarFieldSx,
                overflow: 'visible',
              }}
              onChange={event => {
                handleFilterChange({
                  interval: event.target.value as ChartsTimeInterval,
                });
              }}
            />
            {filters?.interval === 'custom' && (
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale='en'
              >
                <DatePicker
                  name='from_date'
                  label='From'
                  value={filters?.from_date ? dayjs(filters.from_date) : null}
                  onChange={val => {
                    const formattedValue = val?.isValid()
                      ? dayjs(val).format('YYYY-MM-DD')
                      : '';

                    handleFilterChange({
                      from_date: formattedValue,
                    });
                  }}
                  sx={toolbarFieldSx}
                />
                <DatePicker
                  name='to_date'
                  label='To'
                  value={filters.to_date ? dayjs(filters.to_date) : null}
                  minDate={dayjs(filters.from_date).add(1, 'day')}
                  onChange={val => {
                    const formattedValue = val?.isValid()
                      ? dayjs(val).format('YYYY-MM-DD')
                      : '';

                    handleFilterChange({
                      to_date: formattedValue,
                    });
                  }}
                  sx={toolbarFieldSx}
                />
              </LocalizationProvider>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flex: 1,
            justifyContent: 'center',
            gap: 3,
            width: '100%',
            mb: '2rem',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: 'calc(100vh - 200px)',
              display: 'flex',
              flexDirection: 'column',
              gap: '2rem',
              overflow: 'auto',
            }}
          >
            <Box sx={{ display: 'flex', gap: '1rem', width: '100%' }}>
              <Card
                sx={{
                  display: 'flex',
                  p: '1.5rem',
                  flexDirection: 'column',
                  gap: '1.5rem',
                  overflow: 'unset',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    borderRadius: 0,
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    gap: '0.5rem',
                  }}
                >
                  <AccessTimeFilledIcon
                    sx={{ height: '24px', width: '24px' }}
                    htmlColor={theme.palette.primary.main}
                  />
                  <Typography
                    variant='subtitle2'
                    sx={{
                      color: 'gray',
                    }}
                  >
                    Total minutes
                  </Typography>
                </Box>
                {isLoading ? (
                  <Skeleton
                    variant='text'
                    width='60%'
                    height={40}
                    animation='wave'
                  />
                ) : (
                  <Typography variant='h4'>
                    {(allMetrics?.minutes.activity_minutes ?? 0) +
                      (allMetrics?.minutes.care_notes_minutes ?? 0)}{' '}
                    of {allMetrics?.minutes.expected_minutes}
                  </Typography>
                )}

                {isLoading ? (
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height={8}
                    animation='wave'
                    sx={{ borderRadius: 1 }}
                  />
                ) : (
                  <MultiColorLinearProgress
                    value1={allMetrics?.minutes.care_notes_minutes ?? 0}
                    value2={allMetrics?.minutes.activity_minutes ?? 0}
                    maxValue={
                      allMetrics?.minutes.expected_minutes ??
                      EXPECTED_DAILY_USER_MINUTES
                    }
                  />
                )}

                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          borderRadius: 0,
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          gap: '0.5rem',
                        }}
                      >
                        <svg width='20' height='20'>
                          <rect
                            width='20'
                            height='20'
                            fill={theme.palette.primary.main}
                            rx='2'
                          />
                        </svg>
                        <Typography variant='h6'>
                          {allMetrics?.minutes.activity_minutes}
                        </Typography>
                      </Box>
                      <Typography
                        variant='caption'
                        sx={{
                          color: 'gray',
                        }}
                      >
                        Activity log
                      </Typography>
                    </Box>
                    <Divider
                      orientation='vertical'
                      sx={{ height: '80%', mx: '1rem' }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: 0,
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        gap: '0.5rem',
                      }}
                    >
                      <svg width='20' height='20'>
                        <rect
                          width='20'
                          height='20'
                          fill={theme.palette.secondary.main}
                          rx='2'
                        />
                      </svg>
                      <Typography variant='h6'>
                        {allMetrics?.minutes.care_notes_minutes}
                      </Typography>
                    </Box>
                    <Typography
                      variant='caption'
                      sx={{
                        color: 'gray',
                      }}
                    >
                      Care notes
                    </Typography>
                  </Box>
                </Box>
              </Card>

              <Card
                sx={{
                  display: 'flex',
                  p: '1.5rem',
                  flexDirection: 'column',
                  gap: '1.5rem',
                  overflow: 'unset',
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    borderRadius: 0,
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    gap: '0.5rem',
                  }}
                >
                  <Timelapse
                    sx={{ height: '24px', width: '24px' }}
                    htmlColor={theme.palette.primary.main}
                  />
                  <Typography
                    variant='subtitle2'
                    sx={{
                      color: 'gray',
                    }}
                  >
                    Average minutes
                  </Typography>
                </Box>
                {isLoading ? (
                  <Skeleton
                    variant='text'
                    width='60%'
                    height={40}
                    animation='wave'
                  />
                ) : (
                  <Typography variant='h4'>
                    {(allMetrics?.average_minutes.activity_minutes ?? 0) +
                      (allMetrics?.average_minutes.care_notes_minutes ??
                        0)}{' '}
                    of {allMetrics?.average_minutes.expected_minutes}
                  </Typography>
                )}

                {isLoading ? (
                  <Skeleton
                    variant='rectangular'
                    width='100%'
                    height={8}
                    animation='wave'
                    sx={{ borderRadius: 1 }}
                  />
                ) : (
                  <MultiColorLinearProgress
                    value1={allMetrics?.average_minutes.care_notes_minutes ?? 0}
                    value2={allMetrics?.average_minutes.activity_minutes ?? 0}
                    maxValue={
                      allMetrics?.average_minutes.expected_minutes ??
                      EXPECTED_DAILY_USER_MINUTES
                    }
                  />
                )}

                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          borderRadius: 0,
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          gap: '0.5rem',
                        }}
                      >
                        <svg width='20' height='20'>
                          <rect
                            width='20'
                            height='20'
                            fill={theme.palette.primary.main}
                            rx='2'
                          />
                        </svg>
                        <Typography variant='h6'>
                          {allMetrics?.average_minutes.activity_minutes}
                        </Typography>
                      </Box>
                      <Typography
                        variant='caption'
                        sx={{
                          color: 'gray',
                        }}
                      >
                        Activity log
                      </Typography>
                    </Box>
                    <Divider
                      orientation='vertical'
                      sx={{ height: '80%', mx: '1rem' }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      width: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: 0,
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        gap: '0.5rem',
                      }}
                    >
                      <svg width='20' height='20'>
                        <rect
                          width='20'
                          height='20'
                          fill={theme.palette.secondary.main}
                          rx='2'
                        />
                      </svg>
                      <Typography variant='h6'>
                        {allMetrics?.average_minutes.care_notes_minutes}
                      </Typography>
                    </Box>
                    <Typography
                      variant='caption'
                      sx={{
                        color: 'gray',
                      }}
                    >
                      Care notes
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Box>

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                flexDirection: 'column',
              }}
            >
              {chunk(taskMetricsList, isMdSizeUp ? 4 : 2).map(
                (chunk, chunkIdx) => {
                  return (
                    <Card
                      sx={{
                        display: 'flex',
                        p: '1rem',
                        borderRadius: 0,
                        width: '100%',
                      }}
                      key={chunkIdx}
                    >
                      {chunk.map((task, idx) => {
                        return (
                          <React.Fragment key={`${task.label} ${idx}`}>
                            <Box
                              key={`${task.label}-${idx}`}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flex: 1,
                                flexDirection: 'column',
                                gap: '1rem',
                              }}
                            >
                              <Box
                                sx={{
                                  borderRadius: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  width: '100%',
                                  gap: '0.5rem',
                                }}
                              >
                                {task.icon}
                                <Typography variant='subtitle2'>
                                  {task.label}
                                </Typography>
                              </Box>
                              {isLoading ? (
                                <Skeleton
                                  variant='text'
                                  width='50%'
                                  height={40}
                                  animation='wave'
                                />
                              ) : (
                                <Typography
                                  variant='h4'
                                  sx={{ width: '100%', textAlign: 'start' }}
                                >
                                  {formatCount(task.value ?? 0)}
                                </Typography>
                              )}
                            </Box>
                            {idx < chunk.length - 1 && (
                              <Divider
                                orientation='vertical'
                                sx={{ height: '100%', mx: '1rem' }}
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </Card>
                  );
                },
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                  width: '100%',
                  [theme.breakpoints.up('lg')]: { flexDirection: 'row' },
                }}
              >
                <Box sx={{ display: 'flex', gap: '1rem', flex: 6 }}>
                  <Card
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      p: '1.5rem',
                      gap: '1rem',
                      flex: 1,
                    }}
                  >
                    <Typography variant='subtitle2'>
                      Assigned patients
                    </Typography>
                    {isLoading ? (
                      <Skeleton
                        variant='text'
                        width='50%'
                        height={40}
                        animation='wave'
                      />
                    ) : (
                      <Typography variant='h4'>
                        {formatCount(allMetrics?.patient_count ?? 0)}
                      </Typography>
                    )}
                  </Card>
                  <Card
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      p: '1.5rem',
                      gap: '1rem',
                      flex: 1,
                    }}
                  >
                    <Typography variant='subtitle2'>
                      Care plan updates
                    </Typography>
                    {isLoading ? (
                      <Skeleton
                        variant='text'
                        width='50%'
                        height={40}
                        animation='wave'
                      />
                    ) : (
                      <Typography variant='h4'>
                        {formatCount(allMetrics?.care_plans_update_count ?? 0)}
                      </Typography>
                    )}
                  </Card>
                  <Card
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      p: '1.5rem',
                      gap: '1rem',
                      flex: 1,
                    }}
                  >
                    <Typography variant='subtitle2'>
                      Care notes added
                    </Typography>
                    {isLoading ? (
                      <Skeleton
                        variant='text'
                        width='50%'
                        height={40}
                        animation='wave'
                      />
                    ) : (
                      <Typography variant='h4'>
                        {formatCount(allMetrics?.care_notes_written_count ?? 0)}
                      </Typography>
                    )}
                  </Card>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flex: 4 }}>
                  <Card
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      p: '1.5rem',
                      gap: '1rem',
                      flex: 1,
                    }}
                  >
                    <Typography variant='subtitle2'>
                      Unique task edit
                    </Typography>
                    {isLoading ? (
                      <Skeleton
                        variant='text'
                        width='50%'
                        height={40}
                        animation='wave'
                      />
                    ) : (
                      <Typography variant='h4'>
                        {formatCount(allMetrics?.unique_task_edits_count ?? 0)}
                      </Typography>
                    )}
                  </Card>

                  <Card
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      p: '1.5rem',
                      gap: '1rem',
                      flex: 1,
                    }}
                  >
                    <Typography variant='subtitle2'>
                      Unique patient updates
                    </Typography>
                    {isLoading ? (
                      <Skeleton
                        variant='text'
                        width='50%'
                        height={40}
                        animation='wave'
                      />
                    ) : (
                      <Typography variant='h4'>
                        {formatCount(
                          allMetrics?.unique_patient_updates_count ?? 0,
                        )}
                      </Typography>
                    )}
                  </Card>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <ChartCard
                title='CCM Patients Minutes'
                report_id={ccmPatientsByMinutes?.linked_report_id}
                hideButton={!hasViewPermission}
                isLoading={isLoading}
              >
                <DonutChart
                  preserveOrder
                  otherThreshold={6}
                  entries={[
                    {
                      value: ccmPatientsByMinutes?.patients_with_0_minutes ?? 0,
                      label: 'Patients with 0 minutes',
                    },
                    {
                      value:
                        ccmPatientsByMinutes?.patients_with_less_than_20_minutes ??
                        0,
                      label: 'Patients with 1-19 minutes',
                    },
                    {
                      value:
                        ccmPatientsByMinutes?.patients_with_less_than_60_minutes ??
                        0,
                      label: 'Patients with 20-59 minutes',
                    },
                    {
                      value:
                        ccmPatientsByMinutes?.patients_with_less_than_120_minutes ??
                        0,
                      label: 'Patients with 60-119 minutes',
                    },
                    {
                      value:
                        ccmPatientsByMinutes?.patients_with_more_than_120_minutes ??
                        0,
                      label: 'Patients with 120+ minutes',
                    },
                  ]}
                />
              </ChartCard>

              <ChartCard
                title='Patients in Care Management'
                report_id={allMetrics?.care_management_linked_report_id}
                hideButton={!hasViewPermission}
                isLoading={isLoading}
              >
                <DonutChart entries={careManagementPatientEntries ?? []} />
              </ChartCard>
              <ChartCard
                title='Patients in Service Lines'
                report_id={allMetrics?.service_line_linked_report_id}
                hideButton={!hasViewPermission}
                isLoading={isLoading}
              >
                <DonutChart entries={serviceLinePatientEntries ?? []} />
              </ChartCard>
              <ChartCard
                title='GUIDE status'
                report_id={allMetrics?.guide_linked_report_id}
                hideButton={!hasViewPermission}
                isLoading={isLoading}
              >
                <DonutChart entries={guideStatusPatientsEntries ?? []} />
              </ChartCard>
              <ChartCard
                title='GUIDE tier'
                report_id={allMetrics?.guide_linked_report_id}
                hideButton={!hasViewPermission}
                isLoading={isLoading}
              >
                <DonutChart entries={guideTierPatientsEntries ?? []} />
              </ChartCard>
              <ChartCard
                title='Optum MA status'
                report_id={allMetrics?.memory_connect_linked_report_id}
                hideButton={!hasViewPermission}
                isLoading={isLoading}
              >
                <DonutChart
                  entries={memoryConnectStatusPatientsEntries ?? []}
                />
              </ChartCard>
              <ChartCard
                title='Optum MA tier'
                report_id={allMetrics?.memory_connect_linked_report_id}
                hideButton={!hasViewPermission}
                isLoading={isLoading}
              >
                <DonutChart entries={memoryConnectTierPatientsEntries ?? []} />
              </ChartCard>
            </Box>
          </Box>
          <TeamAvailability />
        </Box>
      </Box>
    </NonAdminDashboardContext.Provider>
  );
};
